import {
  Box,
  FormLabel,
  Grid,
  Typography,
  Button,
  debounce,
  DialogContent,
  Stack,
  DialogActions,
  DialogTitle,
  FormControl,
  Checkbox,
  Radio,
  FormControlLabel,
  InputAdornment,
  Dialog,
  Divider,
  MenuItem,
  TextField,
  IconButton,
} from '@mui/material';
import { useEffect, useMemo, useState, useRef } from 'react';
import RowTypography from 'components/Table/RowTypography';
import { useNavigate } from 'react-router-dom';
import eventEmitter from 'SnackBarEventEmitter';
import {
  createDataPipelineProject,
  deleteDataPipelineProgram,
  deleteDataPipelineProject,
  deleteDataPipelineSchedule,
  editDataPipelineProject,
  exportProjectListByProgram,
  getClarityProjectList,
  getProgramById,
  getProgramSectorList,
  getProjectOwnerList,
  getStageList,
  exportIbpStageList,
  upsertIbpStage,
} from 'service/service';
import { fetchLookupData } from './GDOReferenceData';
import CustomTextField from 'components/Inputs/CustomTextField';
import CustomAutocomplete from 'components/Inputs/CustomAutocomplete';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import DeleteImage from '../../assets/delete-img-modal.svg';
import moment from 'moment';
import ShowInfoWidget from 'components/Widgets/ShowInfoWidget';
import { checkRole } from 'utils/roleUtils';
import { usePepwiseAuthController } from 'Context/contexts/AuthContext';
import { Search } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import { TIMEZONE_MAP } from 'utils/common-util';
import {
  getAutocompleteCommonComponentsProps,
  getCommonCustomTextFieldProps,
  getInputCustomCommonSx,
  parseFilter,
  getPaginationSlice,
} from './DataPipelineHelper';
import info from '../../assets/info-icon.svg';

const getDefaultStageListDateRange = () => {
  const ianaTimezone = TIMEZONE_MAP.CST;
  const today = moment.tz(ianaTimezone);

  return {
    dateTimeRangeType: 'today',
    timeZone: 'CST',
    startTime: today.startOf('day').format('DD-MM-YYYY HH:mm:ss'),
    endTime: today.endOf('day').format('DD-MM-YYYY HH:mm:ss'),
  };
};

const getStageColumns = ({
  handleAction,
}: {
  handleAction: (module: string, action: string, row: any, event: any) => void;
}) => {
  const textCell = (field: string) => ({ row }: any) => row[field] || '--';
  const createdDateCell = (field: string) => ({ row }: any) => {
    const value = row[field];
    if (!value) return '--';
    return moment.utc(value).tz('America/Chicago').format('YYYY-MM-DD');
  };
  const chipCell = (field: string) => ({ row }: any) => {
    let values: string[] = [];
    if (Array.isArray(row[field])) {
      values = row[field];
    } else if (row[field]) {
      values = [row[field]];
    }
    return values.length ? values.map((value: string) => (
      <RowTypography
        key={value}
        title={value}
        sx={{
          display: 'inline-flex',
          width: 'auto',
          backgroundColor: '#D9ECFA',
          color: '#35658A',
          padding: '4px 10px',
          marginRight: '5px',
          borderRadius: '20px',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </RowTypography>
    )) : '--';
  };

  return [
    { key: 'sectors', name: 'Sector', formatter: chipCell('sectors'), minWidth: 150 },
    { key: 'reports', name: 'Reports', formatter: chipCell('reports'), minWidth: 150 },
    { key: 'markets', name: 'Markets', formatter: chipCell('markets'), minWidth: 150 },
    { key: 'origin', name: 'Origin', formatter: textCell('origin'), minWidth: 150 },
    { key: 'destination', name: 'Destination', formatter: textCell('destination'), minWidth: 150 },
    { key: 'stageInboundOutbound', name: 'Stage Inbound/Outbound', formatter: textCell('stageInboundOutbound'), minWidth: 220 },
    { key: 'projectName', name: 'Stage Name', formatter: textCell('projectName'), minWidth: 220 },
    { key: 'projectFriendlyName', name: 'Alias (Friendly Name)', formatter: textCell('projectFriendlyName'), minWidth: 220 },
    { key: 'projectOwner', name: 'Owner', formatter: textCell('projectOwner'), minWidth: 220 },
    { key: 'projectDescription', name: 'Description', formatter: textCell('projectDescription'), minWidth: 260 },
    { key: 'gdoStep', name: 'GDO Step', formatter: textCell('gdoStep'), minWidth: 160 },
    { key: 'slaStatus', name: 'SLA Status', formatter: textCell('slaStatus'), minWidth: 180 },
    { key: 'dataSteward', name: 'Data Steward', formatter: textCell('dataSteward'), minWidth: 180 },
    { key: 'dataOwner', name: 'Data Owner', formatter: textCell('dataOwner'), minWidth: 180 },
    { key: 'originalSourceDataAvailabilityCst', name: 'Original Source Data Availability CST', formatter: textCell('originalSourceDataAvailabilityCst'), minWidth: 280 },
    { key: 'itSourceContact', name: 'IT Source Contact', formatter: textCell('itSourceContact'), minWidth: 180 },
    { key: 'dataSourceofExtraction', name: 'Data Source of Extraction', formatter: textCell('dataSourceofExtraction'), minWidth: 180 },
    { key: 'sourceOwnerContact', name: 'Source Owner Contact', formatter: textCell('sourceOwnerContact'), minWidth: 180 },
    { key: 'createdAt', name: 'Created Date (CST)', formatter: createdDateCell('createdAt'), minWidth: 160 },
    {
      key: 'action',
      name: 'Action',
      minWidth: 100,
      resizable: false,
      sortable: false,
      formatter: ({ row }: any) => (
        <Stack direction="row" spacing={1} justifyContent="center">
          <EditIcon
            aria-label="edit-stage"
            onClick={(event) => handleAction('Project', 'edit', row, event)}
            sx={{ fontSize: 18, color: '#009BD8', cursor: 'pointer' }}
          />
          <DeleteIcon
            aria-label="delete-stage"
            onClick={(event) => handleAction('Project', 'delete', row, event)}
            sx={{ fontSize: 18, color: '#E53935', cursor: 'pointer' }}
          />
        </Stack>
      ),
    },
  ];
};

interface ProjectQueryParams {
  filterInfo?: any;
  currentFilterType?: string;
  page?: number;
  pageSize?: number;
  stDate?: string;
  edDate?: string;
  sortingInfo?: any;
  sectorId?: string;
  projName?: string;
}

const mapStageRecord = (record: any) => ({
  ...record,
  Project_Id: record.projectId,
  Project_ID: record.projectId,
  Project_Name: record.projectName,
  Project_Owner: record.projectOwner,
  Project_Description: record.projectDescription,
  Friendly_Name: record.projectFriendlyName,
  sectors: record.sectors || record.sector || [],
  reports: record.reports || record.report || [],
  markets: record.markets || record.market || [],
  createdAt: record.createdAt || '',
});

const getStageFilterData = (filterOptions: any = {}) => {
  const filtersData: any = {
    Sectors: [],
    Project_Name: [],
    SLA_Status: [],
  };
  const filterKeyMap: Record<string, string> = {
    sector: 'Sectors',
    Sector_Name: 'Sectors',
    Project_Name: 'Project_Name',
    projectName: 'Project_Name',
    SLA_Status: 'SLA_Status',
    projectSlaStatus: 'SLA_Status',
    Project_Owner: 'Project_Owner',
    projectOwner: 'Project_Owner',
    Created_Dt: 'Created_Dt',
    createdDate: 'Created_Dt',
  };

  Object.entries(filterOptions).forEach(([key, value]) => {
    const filterKey = filterKeyMap[key];
    if (!filterKey) return;
    filtersData[filterKey] = filterKey === 'SLA_Status'
      ? (value as string[]).filter(Boolean)
      : value;
  });

  return filtersData;
};

const getSectorOptions = (
  isGdoMode: boolean,
  lookupOptions: { sectors: string[] },
  sectorList: any[],
) => {
  if (isGdoMode) return lookupOptions.sectors;

  return sectorList
    .filter((sector) => sector.Sector_Name !== 'SODASTREAM')
    .map((sector) => sector.Sector_Name);
};

const marketList: any = [
  {
    id: 1,
    name: 'Europe',
  }
];

const processList: any = [
  {
    id: 1,
    name: 'S&OE',
  },
  {
    id: 2,
    name: 'S&OP',
  },
];

const getProjectOwnerField = ({
  isClarityProject,
  programData,
  formData,
  handleChange,
  handleInputChange,
  ownerList,
  isDisabled,
  dropdownLoading,
  colors,
  isGdoMode,
  programName,
}: any) => {
  const isEdit = programData?.action === 'EDIT';
  const isGdoOwner = isGdoMode || String(programName || '').trim().toUpperCase().startsWith('GDO-');

  if (isClarityProject && !isEdit) {
    return (
      <Grid item xs={6}>
        <FormControl fullWidth>
          <CustomTextField
            id="ProjectOwner"
            label="Owner"
            name="projectOwner"
            variant="outlined"
            autoComplete="off"
            value={formData.projectOwner}
            disabled
            required
            onChange={handleChange}
            sx={{
              '& .MuiInputBase-input': {
                color: `${colors.input.text} !important`,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: `${colors.input.border} !important`,
              },
              '& .MuiInputLabel-root': {
                color: `${colors.input.text} !important`,
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: `${colors.input.text} !important`,
              },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: `${colors.input.borderFocus} !important`,
              },
              '& .MuiFormHelperText-root': {
                color: `${colors.input.text} !important`,
              },
              '& .MuiOutlinedInput-root': {
                height: 40,
                backgroundColor: '#F4F8FC',
                '& fieldset': { borderColor: '#71758FCC !important' },
              },
            }}
          />
        </FormControl>
      </Grid>
    );
  }

  return (
    <Grid item xs={6}>
      <FormControl fullWidth>
        {isGdoOwner ? (
          <CustomAutocomplete
            loading={dropdownLoading.owner}
            options={ownerList}
            noOptionsText="No user found"
            value={formData.projectOwner || ''}
            onChange={(event, value) => handleChange(event, value, 'projectOwner')}
            onInputChange={(event, value) => handleInputChange(event, 'projectOwner', value)}
            textFieldLabel="Owner *"
            popupIcon={<ExpandMoreIcon />}
            sx={{
              ...getInputCustomCommonSx(colors),
              width: '100%',
              backgroundColor: '#F4F8FC !important',
              '& .MuiOutlinedInput-root': {
                height: 40,
                backgroundColor: '#F4F8FC !important',
                '& fieldset, & .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#71758FCC !important',
                },
                '&:hover fieldset, &:hover .MuiOutlinedInput-notchedOutline, &.Mui-focused fieldset, &.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#009BD8 !important',
                },
              },
              '& .MuiInputBase-root': {
                color: '#71758F !important',
                backgroundColor: '#F4F8FC !important',
              },
              '& .MuiInputBase-input, & input': {
                color: '#71758F !important',
                WebkitTextFillColor: '#71758F !important',
                padding: '8.5px 14px !important',
              },
              '& .MuiInputLabel-root': {
                color: '#5A6A85 !important',
                '&.Mui-focused, &.MuiInputLabel-shrink': { color: '#003771 !important' },
              },
              '& .MuiInputBase-input::placeholder': {
                color: '#5A6A85 !important',
                opacity: '1 !important',
              },
              '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-popupIndicator .MuiSvgIcon-root, & .MuiAutocomplete-popupIndicator svg': {
                color: '#009BD8 !important',
                fill: '#009BD8 !important',
                width: 20,
                height: 20,
                padding: 0,
              },
            }}
            componentsProps={{
              paper: {
                sx: {
                  backgroundColor: '#D8E7FA !important',
                  color: '#35658A !important',
                  width: 435,
                  maxHeight: 310,
                  overflowY: 'auto',
                  '& .MuiAutocomplete-listbox': {
                    backgroundColor: '#D8E7FA !important',
                    padding: 0,
                  },
                  '& .MuiAutocomplete-option': {
                    minHeight: 46,
                    color: '#35658A !important',
                    backgroundColor: '#D8E7FA !important',
                    '&:hover': { backgroundColor: '#C8DCF5 !important' },
                    '&[aria-selected="true"]': { backgroundColor: '#C8DCF5 !important' },
                    '&[aria-selected="true"]:hover': { backgroundColor: '#BBD3F2 !important' },
                  },
                },
              },
              popper: {
                sx: {
                  '& .MuiAutocomplete-paper, & .MuiPaper-root': {
                    backgroundColor: '#D8E7FA !important',
                    color: '#35658A !important',
                  },
                },
              },
            }}
            customTextFieldProps={{ ...getCommonCustomTextFieldProps(colors), size: 'small' }}
          />
        ) : (
          <CustomAutocomplete
          loading={dropdownLoading.owner}
          options={ownerList}
          noOptionsText="No user found"
          value={formData.projectOwner}
          onChange={(e, val) => handleChange(e, val, 'projectOwner')}
          onInputChange={(e, val) => handleInputChange(e, 'projectOwner', val)}
          disabled={isDisabled.owner || (isClarityProject && programData.action == 'EDIT')}
          textFieldLabel="Owner *"
          popupIcon={<ExpandMoreIcon />}
          sx={{
            ...getInputCustomCommonSx(colors),
            width: '100%',
            backgroundColor: '#F4F8FC !important',
            '& .MuiOutlinedInput-root': {
              height: 40,
              backgroundColor: '#F4F8FC !important',
              '& fieldset, & .MuiOutlinedInput-notchedOutline': {
                borderColor: '#71758FCC !important',
              },
              '&:hover fieldset, &:hover .MuiOutlinedInput-notchedOutline, &.Mui-focused fieldset, &.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#009BD8 !important',
              },
            },
            '& .MuiInputBase-root': {
              color: '#71758F !important',
              backgroundColor: '#F4F8FC !important',
            },
            '& .MuiInputBase-input, & input': {
              color: '#71758F !important',
              WebkitTextFillColor: '#71758F !important',
              padding: '8.5px 14px !important',
            },
            '& .MuiInputLabel-root': {
              color: '#5A6A85 !important',
              '&.Mui-focused, &.MuiInputLabel-shrink': { color: '#003771 !important' },
            },
            '& .MuiInputBase-input::placeholder': {
              color: '#5A6A85 !important',
              opacity: '1 !important',
            },
            '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-popupIndicator .MuiSvgIcon-root, & .MuiAutocomplete-popupIndicator svg': {
              color: '#009BD8 !important',
              fill: '#009BD8 !important',
              width: 20,
              height: 20,
              padding: 0,
            },
          }}
          componentsProps={{
            paper: {
              sx: {
                width: 258,
                maxHeight: '240px !important',
                overflowY: 'auto !important',
                backgroundColor: '#FFFFFF !important',
                color: '#003771 !important',
                border: '1px solid #D2E6F9',
                boxShadow: '0px 4px 16px rgba(0, 55, 113, 0.08)',
                '&.MuiPaper-root': {
                  backgroundColor: '#FFFFFF !important',
                },
                '& .MuiAutocomplete-listbox': {
                  backgroundColor: '#FFFFFF !important',
                  paddingTop: 0,
                  paddingBottom: 0,
                  maxHeight: '238px !important',
                  overflowY: 'auto !important',
                },
                '& .MuiAutocomplete-option': {
                  height: '40px !important',
                  minHeight: '40px !important',
                  boxSizing: 'border-box',
                  color: '#003771 !important',
                  backgroundColor: '#FFFFFF !important',
                  '&:hover': { backgroundColor: '#F4F8FC !important' },
                  '&[aria-selected="true"]': { backgroundColor: '#D8F0FA !important' },
                  '&[aria-selected="true"]:hover': { backgroundColor: '#D8F0FA !important' },
                },
              },
            },
            popper: {
              sx: {
                '& .MuiAutocomplete-paper, & .MuiPaper-root': {
                  width: '258px !important',
                  maxHeight: '240px !important',
                  overflowY: 'auto !important',
                  backgroundColor: '#FFFFFF !important',
                },
                '& .MuiAutocomplete-listbox': {
                  maxHeight: '238px !important',
                  overflowY: 'auto !important',
                  backgroundColor: '#FFFFFF !important',
                },
                '& .MuiAutocomplete-option, & .MuiAutocomplete-option[aria-selected="true"]': {
                  height: '40px !important',
                  minHeight: '40px !important',
                  color: '#003771 !important',
                  backgroundColor: '#FFFFFF !important',
                  '&:hover': { backgroundColor: '#F4F8FC !important' },
                  '&[aria-selected="true"]': { backgroundColor: '#D8F0FA !important' },
                  '&[aria-selected="true"]:hover': { backgroundColor: '#D8F0FA !important' },
                },
              },
            },
          }}
          customTextFieldProps={{ ...getCommonCustomTextFieldProps(colors), size: 'small' }}
          />
        )}
      </FormControl>
    </Grid>
  );
};

export const ProjectTab = ({
  handleEditClose,
  programData,
  programID,
  programName,
  setProjectData,
  projectData,
  isGdoMode,
}: any) => {
  const theme = useTheme();
  const colors = theme.palette;
  const [sectorList, setSectorList] = useState<any[]>([]);
  const [lookupOptions, setLookupOptions] = useState({
    sectors: [] as string[],
    markets: [] as string[],
    reports: [] as string[],
  });
  const [loading, setLoading] = useState<any>({
    sector: false,
    project: false,
  });
  const [saveBtnLoader, setSaveBtnLoader] = useState(false);
  const [isDisabled, setIsDisabled] = useState<any>({
    description: false,
    owner: false,
  });
  const [inputValue, setInputValue] = useState('');
  const [ownerList, setOwnerList] = useState<any>([]);
  const [isClarityProject, setIsClarityProject] = useState(false);
  const [validSlaTime, setValidSlaTime] = useState(true);
  const [projectList, setProjectList] = useState<any>([]);
  const [projectListForm, setProjectListForm] = useState([]);
  const [open, setOpen] = useState(false);
  const isSlaChanged = false;
  type ProjectOption = {
    label: string;
    id: string;
  };
  const [projectOptionList, setProjectOptionList] = useState<ProjectOption[]>([]);
  const [openModal, setOpenModal] = useState<any>({ isOpen: false, message: '' });

  const isUpdatedFlag = useRef(false);
  const isInitialized = useRef(false);
  const formContainerRef = useRef<HTMLDivElement>(null);
  const [sectorName, setSectorName] = useState('all');
  const startDate = moment().tz('America/Chicago').startOf('day').format('YYYY-MM-DD');
  const endDate = moment().tz('America/Chicago').endOf('day').format('YYYY-MM-DD');
  const [tableLoading, setTableLoading] = useState(false);
  const [paginationData, setPaginationData] = useState({
    totalElements: 0,
    totalPages: 0,
    currentPage: 0,
  });
  const [filterData, setFilterData] = useState<any[]>([]);
  const [sortingInfo, setSortingInfo] = useState({ sortBy: '', sortDir: '' });
  const [filterInfo, setFilterInfo] = useState([]);
  const currentFilterType = '';
  const sectorMasterList: any[] = [];
  const [searchProject, setSearchProject] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [projectsDataList, setProjectsDataList] = useState<any[]>([]);
  const [programDetail, setProgramDetail] = useState<any>({});
  const [controller] = usePepwiseAuthController();
  const { role } = controller;
  const isAllowed = checkRole(role, ['Data Operations Admin', 'PepWise System User']);
  const searchParams = new URLSearchParams(location.search);
  const type = searchParams.get('type');
  const [dropdownLoading, setDropdownLoading] = useState<any>({
    project: false,
    owner: false,
  });

  const [formData, setFormData] = useState<any>({
    id: '',
    Sectors: [],
    ProgramName: '',
    projectName: '',
    projectDescription: '',
    gdoStep: '',
    projectOwner: '',
    clarityId: '',
    slaDate: 0,
    slaTime: '',
    reqSlaTime: '',
    friendlyName: '',
    dataSteward: '',
    dataOwner: '',
    originalSourceDataAvailabilityCST: '',
    itSourceContact: '',
    dataSourceOfExtraction: '',
    sourceOwnerContact: '',
    isSlaConfigured: false,
    slaType: '',
    slaData: [],
    Market: [],
    Process: [],
  });
  const sectorOptions = getSectorOptions(isGdoMode, lookupOptions, sectorList);

  // table data
  const tableData: any[] = [];
  const defaultColumnNames = [
    'Sector',
    'Reports',
    'Markets',
    'Stage Name',
    'Alias (Friendly Name)',
    'Owner',
    'Description',
    'SLA Status',
    'Created Date (CST)',
    'Action',
  ];

  const queryParams = new URLSearchParams(location.search);
  const isEdited = queryParams.get('isEdited') === 'true';
  const projectIdFromURL = queryParams.get('projectID');
  const [toast, setToast] = useState<any>({
    open: false,
    message: '',
  });

  const handleThreeDotsClose = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDetailsThreeDotsClose = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const buildSlaTime = (value: number | null | undefined): string => {
    if (value == null) return '';
    let reqslatime = '';
    if (value > 0) {
      if (value < 10) {
        reqslatime = `000${value}`.replace(/(.{2})$/, ':$1');
      } else if (value < 100) {
        reqslatime = `00${value}`.replace(/(.{2})$/, ':$1');
      } else {
        reqslatime = value.toString().replace(/(.{2})$/, ':$1');
      }
    } else if (value === 0) {
      reqslatime = '00:00';
    } else {
      reqslatime = 'NA';
    }
    return reqslatime;
  };

  const commonInputSx = {
    '& .MuiInputBase-input': {
      color: `${colors.input.text} !important`,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: `${colors.input.border} !important`,
    },
    '& .MuiInputLabel-root': {
      color: `${colors.input.text} !important`,
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: `${colors.input.text} !important`,
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: `${colors.input.borderFocus} !important`,
    },
    '& .MuiFormHelperText-root': {
      color: `${colors.input.text} !important`,
    },
  };

  const stageInputSx = {
    width: '100%',
    backgroundColor: '#F4F8FC',
    borderRadius: '4px',
    '& .MuiInputBase-input': {
      color: '#003771 !important',
      WebkitTextFillColor: '#003771 !important',
    },
    '& .MuiInputLabel-root': {
      color: '#5A6A85 !important',
      '&.Mui-focused, &.MuiInputLabel-shrink': { color: '#003771 !important' },
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#F4F8FC',
      '& fieldset': { borderColor: '#71758FCC !important' },
      '&:hover fieldset, &.Mui-focused fieldset': { borderColor: '#009BD8 !important' },
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#71758FCC !important' },
    '& .MuiSelect-select': { color: '#003771 !important' },
    '& .MuiSelect-icon': { color: '#009BD8 !important' },
  };

  const scrollToTop = () => {
    setTimeout(() => {
      formContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };
  const normalizeOptionValue = (value: any): any => {
    if (typeof value === 'string') return value.trim();
    return value;
  };

  const handleAction = (module: string, type: string, data: any, e: any) => {
    if (e?.preventDefault) {
      e.preventDefault();
      e.stopPropagation();
      handleThreeDotsClose(e);
      handleDetailsThreeDotsClose(e);
    }
    data = {
      ...data,
      ProgramName: programDetail?.program_Name || programName,
      ProgramID: data.Program_ID || data.programId,
      Program_Name: data.Program_Name || data.projectFriendlyName || programName,
      Program_ID: data.Program_ID || data.programId,
      Project_Name: data.Project_Name || data.projectName,
      Project_Id: data.Project_Id || data.projectId,
      Project_Owner: data.Project_Owner || data.projectOwner,
      Project_Description: data.Project_Description || data.projectDescription,
      Friendly_Name: data.Friendly_Name || data.projectFriendlyName,
    };
    let reqslatime = '';
    if (type === 'edit') {
      let slaTimeValue = programData.data?.slaTime;
      let SLA_TimeValue = programData.data?.SLA_Time;
      const rawValue = slaTimeValue ?? SLA_TimeValue;
      reqslatime = buildSlaTime(rawValue);
    }

    switch (type) {
      case 'edit': {
        programData.action = 'EDIT';
        const toList = (value: any): any[] => {
          if (Array.isArray(value)) return value;
          if (value) return value.split(', ');
          return [];
        };
        const stageName = isGdoMode
          ? data.stageInboundOutbound || data.Stage_Inbound_Outbound || ''
          : data.Project_Name || data.projectName || '';
        const sectors = toList(data.sectors ?? data.Sectors).map(normalizeOptionValue);
        const markets = toList(data.markets ?? data.Market).map(normalizeOptionValue);
        const reports = toList(data.reports ?? data.Process).map(normalizeOptionValue);

        setOpenModal({
          ...openModal,
          isOpen: false,
          type: module,
          action: 'EDIT',
          data: data,
        });
        setFormData({
          projectDescription: data.Project_Description || data.projectDescription,
          gdoStep: data.gdoStep || data.Gdo_Step || data.GDO_Step || '',
          projectOwner: data.Project_Owner || data.projectOwner,
          projectId: data.Project_Id || data.projectId,
          origin: data.Origin || data.origin || '',
          destination: data.Destination || data.destination || '',
          projectName: stageName,
          Sectors: sectors,
          friendlyName: data.Friendly_Name || data.projectFriendlyName,
          dataSteward: data.Data_Steward || data.dataSteward || '',
          dataOwner: data.Data_Owner || data.dataOwner || '',
          originalSourceDataAvailabilityCST:
            data.Original_Source_Data_Availability_CST ||
            data.originalSourceDataAvailabilityCst ||
            data.originalSourceDataAvailabilityCST ||
            '',
          itSourceContact: data.IT_Source_Contact || data.itSourceContact || '',
          dataSourceOfExtraction:
            data.Data_Source_of_Extraction ||
            data.dataSourceofExtraction ||
            data.dataSourceOfExtraction ||
            '',
          sourceOwnerContact: data.Source_Owner_Contact || data.sourceOwnerContact || '',
          Market: markets,
          Process: reports,
          slaTime: data?.slaType == 1 ? null : `2024-07-21T${reqslatime}`,
          slaDate: data?.slaType == 1 ? 0 : data?.dateValue || 0,
          reqSlaTime: data?.slaType == 1 ? null : data?.SLA_Time,
          slaType: data?.slaType == 1 ? 'weekly' : 'daily',
          isSlaConfigured: data?.Is_Sla_Configured == 1,
        });
        setIsClarityProject(!!data.Clarity_Project_Id);
        // Scroll to form on edit
        scrollToTop();
        break;
      }
      case 'delete':
        programData.action = 'DELETE';
        programData.isOpen = true;
        programData.data = data;
        setOpenModal({
          ...openModal,
          isOpen: true,
          type: module,
          action: 'DELETE',
          data: data,
        });
        break;
      case 'view':
        window.location.href = `/data-pipeline/ibp-dashboard/projects/${data.Program_ID}/${
          data.Project_Id
        }/${encodeURIComponent(data.ProgramName)}/${encodeURIComponent(data.Project_Name)}`;
        break;
      default:
        break;
    }
  };

  const navigate = useNavigate();

  eventEmitter.on('showSnackbar', (message: string) => {
    setLoading(false);
    setDropdownLoading({
      project: false,
      owner: false,
    });
  });

  const debouncedFetchOptionsProjectSearch = useMemo(
    () =>
      debounce(async (value) => {
        getProjectsByProgram({
          filterInfo: parseFilter(filterInfo),
          currentFilterType,
          page: 1,
          pageSize: 100,
          stDate: startDate,
          edDate: endDate,
          sortingInfo: { sortBy: '', sortDir: '' },
          sectorId: getSectorId(),
          projName: searchProject || value,
        });
      }, 500),
    [],
  );

  const debouncedFetchOptions = useMemo(
    () =>
      debounce(async (value, type) => {
        setLoading({
          ...loading,
          project: true,
        });
        if (type === 'projectName') {
          getProjectList(value);
        } else {
          getOwnerList(value);
        }
        setLoading({
          ...loading,
          project: false,
        });
      }, 500),
    [],
  );

  useEffect(() => {
    if (!isInitialized.current) {
      isInitialized.current = true;
      resetAndRefresh();
    }
  }, []);

  useEffect(() => {
    if (
      (programData?.type?.toLowerCase() === 'project' || type?.toLowerCase() === 'project') &&
      inputValue.length > 2
    ) {
      debouncedFetchOptions(inputValue, 'projectName');
    }
    let sectorId;
    if (sectorName && sectorName != 'all' && sectorMasterList?.length > 0) {
      sectorId = sectorMasterList.filter((s: any) => s.Sector_Name == sectorName)[0].Sector_Id;
    }
    setSearchProject('');
  }, [inputValue, debouncedFetchOptions]);

  const getSectorId = (): string | undefined => {
    let sectorId;
    let selectedSctr = localStorage.getItem('selectedSector');
    if (selectedSctr && selectedSctr != 'all') {
      setSectorName(selectedSctr);
      const sectorList = JSON.parse(localStorage.getItem('sectorsMasterData') ?? '');
      if (sectorList) {
        sectorId = sectorList.filter((s: any) => s.Sector_Name == selectedSctr)[0].Sector_Id;
      }
    }
    return sectorId;
  };

  const getProjectsByProgram = async ({
    filterInfo,
    currentFilterType,
    page,
    pageSize,
    stDate,
    edDate,
    sortingInfo,
    sectorId,
    projName,
  }: ProjectQueryParams = {}) => {
    setTableLoading(true);
    try {
      const payload = {
        paginationInfo: {
          page: Number(page || 1),
          pageSize: Number(pageSize || 100),
        },
        searchText: projName?.trim() || '',
        sortingInfo: {
          sortBy: sortingInfo?.sortBy || 'projectName',
          sortDir: sortingInfo?.sortDir || 'asc',
        },
        globalFilters: {},
        dateRange: getDefaultStageListDateRange(),
        programId: programID,
      };

      const result = await getStageList(payload);
      const data = result?.data || result || {};
      const stageData = Array.isArray(data.records) ? data.records : [];
      const stageRows = stageData.map(mapStageRecord);
      setProjectsDataList(stageRows);
      const totalElements = Number(data.totalRecords) || stageRows.length;
      const totalPages = Math.ceil(totalElements / Number(pageSize || 100));
      setPaginationData({ totalElements, totalPages, currentPage: 1 });
      setPage(1);
      setProjectList(stageRows.slice(0, Number(pageSize)));
      const filtersData = getStageFilterData(data.filterOptions);
      setFilterData(filtersData);
      setFilterInfo(filterInfo);
      setProjectData(stageRows);
      if (projectIdFromURL && stageRows.length > 0) {
        const matchingRow = stageRows.find(
          (row: any) => row.Project_Id?.toString() === projectIdFromURL,
        );
        if (matchingRow && !isUpdatedFlag.current) {
          handleAction('Project', 'edit', matchingRow, null);
        }
        isUpdatedFlag.current = false;
      }
    } catch (error) {
      console.error('Failed to fetch stage list', error);
      setProjectsDataList([]);
      setProjectList([]);
      setProjectData([]);
      setPaginationData({ totalElements: 0, totalPages: 0, currentPage: 1 });
    } finally {
      setTableLoading(false);
    }
  };

  const getOwnerList = async (name = '') => {
    setDropdownLoading({
      ...dropdownLoading,
      owner: true,
    });
    let result = await getProjectOwnerList(name);
    setDropdownLoading({
      ...dropdownLoading,
      owner: false,
    });
    let owner: any = [];
    result.data.projectOwnerNames.map((obj: string) => {
      let formattedData = (obj.includes('null') ? obj.replace('(null)', '') : obj).trim();
      owner.push(formattedData);
    });
    setOwnerList(owner);
  };

  const getProjectList = async (name = '') => {
    setDropdownLoading({
      ...dropdownLoading,
      project: true,
    });
    let result = await getClarityProjectList(name);
    setDropdownLoading({
      ...dropdownLoading,
      project: false,
    });
    setProjectListForm(result.data.projectList);
    setProjectOptionList(
      result.data.projectList.map((obj: any) => {
        return { label: obj.Project_Name, id: obj.Project_Code };
      }),
    );
  };

  const handleInputChange = (event: any, name: any, newInputValue: any) => {
    if (name === 'projectName') {
      setInputValue(newInputValue);
      setFormData({
        ...formData,
        [name]: newInputValue,
      });
    } else if (name === 'projectOwner') {
      setFormData({
        ...formData,
        [name]: newInputValue,
      });
      if (event?.type == 'change') {
        debouncedFetchOptions(newInputValue, name);
      }
    } else {
      setFormData({
        ...formData,
        [name]: newInputValue,
      });
    }
  };

  const getSectorData = async () => {
    setLoading({
      ...loading,
      sector: true,
    });
    if (isGdoMode) {
      const lookupData = await fetchLookupData();
      setLookupOptions({
        sectors: lookupData.sectors,
        markets: lookupData.markets,
        reports: lookupData.reports,
      });
    } else {
      let sectors: any = await getProgramSectorList();
      setSectorList(sectors.data.sectors);
    }
    setLoading({
      ...loading,
      sector: false,
    });
  };

  const editProject = async (slaDataChange?: boolean) => {
    if (isGdoMode) {
      return upsertIbpStage({
        programId: programID,
        projectData: {
          projectDescription: formData.projectDescription,
          projectId: formData.projectId || '',
          projectOwner: formData.projectOwner,
          projectFriendlyName: formData.friendlyName,
          sectors: formData.Sectors,
          markets: formData.Market,
          reports: formData.Process,
          attributes: {
            gdoStep: formData.gdoStep || '',
            dataSteward: formData.dataSteward,
            dataOwner: formData.dataOwner,
            originalSourceDataAvailabilityCst: formData.originalSourceDataAvailabilityCST,
            dataSourceofExtraction: formData.dataSourceOfExtraction,
            sourceOwnerContact: formData.sourceOwnerContact,
            itSourceContact: formData.itSourceContact,
            origin: formData.origin,
            destination: formData.destination,
            stageInboundOutbound: formData.projectName || '',
          },
        },
      });
    }

    let data = {
      id: formData.projectId,
      projectOwner: formData.projectOwner,
      projectName: formData.projectName,
      projectDescription: formData.projectDescription,
      mulSector: formData.Sectors,
      friendlyName: formData.friendlyName,
      origin: formData.origin,
      destination: formData.destination,
      dataSteward: formData.dataSteward,
      dataOwner: formData.dataOwner,
      originalSourceDataAvailabilityCST: formData.originalSourceDataAvailabilityCST,
      itSourceContact: formData.itSourceContact,
      dataSourceOfExtraction: formData.dataSourceOfExtraction,
      sourceOwnerContact: formData.sourceOwnerContact,
      isSlaConfigured: formData?.isSlaConfigured,
      slaType: (() => {
        if (formData?.slaType == 'weekly') return 1;
        if (formData?.slaType == 'daily') return 0;
        return '';
      })(),
      slaData: formData?.slaType == 'daily' ? [] : tableData,
      slaDate: formData.slaDate,
      slaTime: formData.reqSlaTime,
      isSlaChanged: slaDataChange || isSlaChanged,
    };
    let sectorIds: string[] = [];
    for (const sector of sectorList) {
      if (formData.Sectors.includes(sector.Sector_Name)) {
        sectorIds.push(sector.Sector_Id);
      }
    }
    data['mulSector'] = sectorIds;
    await editDataPipelineProject(data);
    setFormData(initialFormData);
  };

  const createProject = async (data: any) => {
    let result = await createDataPipelineProject(data);

    setSaveBtnLoader(false);
    return result;
  };

  const validateAliasInput = (event: any) => {
    const allowedChars = /^[a-zA-Z0-9\s_-]*$/;
    if (!allowedChars.test(event.key)) {
      event.preventDefault();
    }
  };

  const handlePaste = (event: any) => {
    event.preventDefault();
  };

  const getToastData = (data: any) => {
    setToast({
      ...toast,
      message: data.data.message,
    });
  };

  const handleCreateAction = async (
    formData: any,
    ownerList: any[],
    isClarityProject: boolean,
    tableData: any[],
    sectorList: any[],
  ) => {
    if (isGdoMode) {
      const result = await upsertIbpStage({
        programId: programID,
        projectData: {
          projectDescription: formData.projectDescription,
          projectId: '',
          projectOwner: formData.projectOwner,
          projectFriendlyName: formData.friendlyName,
          sectors: formData.Sectors,
          markets: formData.Market,
          reports: formData.Process,
          attributes: {
            gdoStep: formData.gdoStep || '',
            dataSteward: formData.dataSteward,
            dataOwner: formData.dataOwner,
            originalSourceDataAvailabilityCst: formData.originalSourceDataAvailabilityCST,
            dataSourceofExtraction: formData.dataSourceOfExtraction,
            sourceOwnerContact: formData.sourceOwnerContact,
            itSourceContact: formData.itSourceContact,
            origin: formData.origin,
            destination: formData.destination,
            stageInboundOutbound: formData.projectName || '',
          },
        },
      });

      if (result.data?.message) {
        getToastData(result);
      } else if (result.response?.status == 400) {
        getToastData({ data: { message: result.response.data.data.message } });
      }
      return result;
    }

    let index = ownerList.indexOf(formData.projectOwner);

    let formPayload: any = {
      programID: programID,
      projectName: formData?.projectName?.label || formData?.projectName,
      clarityProId: formData?.clarityId || '',
      projectDescription: formData?.projectDescription,
      projectOwner: index >= 0 || isClarityProject ? formData?.projectOwner : '',
      mulSector: formData?.Sectors,
      friendlyName: formData.friendlyName,
      origin: formData.origin,
      destination: formData.destination,
      dataSteward: formData.dataSteward,
      dataOwner: formData.dataOwner,
      originalSourceDataAvailabilityCST: formData.originalSourceDataAvailabilityCST,
      itSourceContact: formData.itSourceContact,
      dataSourceOfExtraction: formData.dataSourceOfExtraction,
      sourceOwnerContact: formData.sourceOwnerContact,
      isSlaConfigured: formData?.isSlaConfigured,
      slaType: (() => {
        if (formData?.slaType == 'weekly') return 1;
        if (formData?.slaType == 'daily') return 0;
        return '';
      })(),
      slaData: formData?.slaType == 'daily' ? [] : tableData,
      slaDate: formData.slaDate,
      slaTime: formData.reqSlaTime,
    };

    let sectorIds: string[] = [];
    for (const sector of sectorList) {
      if (formData.Sectors.includes(sector.Sector_Name)) {
        sectorIds.push(sector.Sector_Id);
      }
    }
    formPayload['mulSector'] = sectorIds;
    let result = await createProject(formPayload);

    if (result.data?.message) {
      const createdProjectId = result.data.projectId;
      formData.projectId = createdProjectId;
      getToastData(result);
    } else if (result.response?.status == 400) {
      getToastData({ data: { message: result.response.data.data.message } });
    }

    return result;
  };

  const handleEditAction = async (slaDataChange?: boolean) => {
    await editProject(slaDataChange);
  };

  const handleDeleteAction = async (programData: any) => {
    if (programData.type === 'Program') {
      return await deleteDataPipelineProgram(
        programData.data.Program_ID || programData.data.Program_id,
      );
    }

    if (programData.type === 'Project') {
      const projectId =
        programData.data.Project_Id || programData.data.projectId || programData.data.Project_ID;
      return await deleteDataPipelineProject(projectId);
    }

    // Delete schedules
    const jobIds = programData.data.map((d: any) => d.jobId);

    const reqBody = {
      bulkProjectId: programData.data[0].Project_Id,
      bulkJobId: jobIds,
      scheduler: programData.data[0].schedulerName,
    };

    return await deleteDataPipelineSchedule(reqBody);
  };

  const resetAndRefresh = () => {
    getProjectList();
    programData.action = 'CREATE';
    programData.isOpen = false;
    programData.type = 'Project';
    getOwnerList('');
    getSectorData();

    let sectorId;
    if (sectorName && sectorName != 'all' && sectorMasterList?.length > 0) {
      sectorId = sectorMasterList.filter((s: any) => s.Sector_Name == sectorName)[0].Sector_Id;
    }
    // getProgramDetails(sectorId);
    let valArray: any = [];
    projectData.length &&
      projectData.map((dat: any) => {
        valArray.push({
          id: 1,
          name: dat.Project_Name,
          sectors: dat.Sectors,
          projectOwner: dat.Project_Owner,
          description: dat.Project_Descrition,
          markets: dat.Market,
          createdDate: dat.Created_At,
          friendlyName: dat.Friendly_Name,
        });
      });

    // setProjectList(valArray);
    setProgramDetail({ projectCount: projectList.length });
    getProjectsByProgram({
      filterInfo: parseFilter(filterInfo),
      currentFilterType,
      page: 1,
      pageSize: 100,
      stDate: startDate,
      edDate: endDate,
      sortingInfo,
      sectorId,
    });
  };

  const onSubmit = async (event: any, isforceUpdate?: boolean, slaDataChange?: boolean) => {
    if (saveBtnLoader) return;
    setSaveBtnLoader(true);
    event.preventDefault();

    switch (programData.action) {
      case 'CREATE':
        await handleCreateAction(formData, ownerList, isClarityProject, tableData, sectorList);
        break;

      case 'EDIT':
        await handleEditAction(slaDataChange);
        break;

      case 'DELETE':
        await handleDeleteAction(programData);
        break;
    }

    setSaveBtnLoader(false);

    handleClose('updated');

    isUpdatedFlag.current = true;
    resetAndRefresh();
    if (isEdited)
      navigate(
        `/data-pipeline/ibpDashboard/gdoTabs?programID=${programID}&programName=${encodeURIComponent(programName || '')}&tab=1&type=Project&projectName=${encodeURIComponent(formData?.projectName || '')}&projectId=${formData?.projectId || ''}&isEdited=true`,
      );
    else
      navigate(
        `/data-pipeline/ibpDashboard/gdoTabs?programID=${programID}&programName=${encodeURIComponent(programName || '')}&tab=1&type=Project&projectName=${encodeURIComponent(formData?.projectName || '')}&projectId=${formData?.projectId || ''}`,
      );
    setFormData(initialFormData);
  };

  const isProjectFormValid = () => {
    return (
      (!isGdoMode || formData.projectName) &&
      (!isGdoMode || formData.projectName?.trim()) &&
      formData.projectName?.length <= 100 &&
      formData.origin?.trim() &&
      formData.destination?.trim() &&
      formData.projectDescription &&
      formData.projectOwner &&
      formData.Sectors &&
      formData.Sectors?.length &&
      formData.friendlyName?.trim() &&
      formData.friendlyName?.length <= 100
    );
  };

  const handleOnBlur = (event: any, type: string) => {
    if (type === 'friendlyName') {
      setFormData({
        ...formData,
        [type]: event.target.value.trim(),
      });
    }
  };

  const handleProcessChange = (event: any, newValue: any, type: string) => {
    const isChecked = event.target.checked;
    const normalizedNewValue = normalizeOptionValue(newValue);
    let updatedValues = (formData[type] || []).map(normalizeOptionValue);

    if (isChecked) {
      const hasMatch = updatedValues.some(
        (item: any) => String(item).trim().toLowerCase() === String(normalizedNewValue).trim().toLowerCase(),
      );
      if (!hasMatch) {
        updatedValues = [...updatedValues, normalizedNewValue];
      }
    }
    if (!isChecked) {
      updatedValues = updatedValues.filter(
        (item: any) => String(item).trim().toLowerCase() !== String(normalizedNewValue).trim().toLowerCase(),
      );
    }

    setFormData({ ...formData, [type]: updatedValues });
  };

  const handleProjectNameChange = (newValue: any) => {
    setIsDisabled({ description: false, owner: false });
    if (!newValue?.id) return;

    const data: any = projectListForm.filter((obj: any) => obj.Project_Code === newValue.id);
    setFormData({
      ...formData,
      projectDescription: data[0]?.Project_Description || '',
      projectOwner: data[0]?.EMPLOYEE_NAME,
      projectName: data[0]?.Project_Name,
      clarityId: data[0]?.Project_Code,
    });
    setIsDisabled({ description: true, owner: true });
  };

  const handleSectorsChange = (checked: boolean, newValue: any) => {
    if (isGdoMode) {
      setFormData({ ...formData, Sectors: checked ? [newValue] : [] });
      return;
    }

    const selectedSectors = [...formData.Sectors];
    if (checked) {
      selectedSectors.push(newValue);
    } else {
      selectedSectors.splice(selectedSectors.indexOf(newValue), 1);
    }
    setFormData({ ...formData, Sectors: selectedSectors });
  };

  const handleSlaTimeChange = (event: any) => {
    const dateObj = new Date(event);
    const hours = dateObj.getHours().toString().padStart(2, '0');
    const minutes = dateObj.getMinutes().toString().padStart(2, '0');
    const isValid = !isNaN(Number(hours)) && !isNaN(Number(minutes));

    setValidSlaTime(isValid);

    if (Number(hours) >= 0 && Number(minutes) >= 0) {
      const formattedDate = dateObj?.toISOString().split('T')[0];
      const reqTime = Number(`${hours}${minutes}`);
      const time = `${formattedDate}T${hours}:${minutes}`;
      setFormData({ ...formData, slaTime: time, reqSlaTime: reqTime });
      return;
    }

    setFormData({ ...formData, slaTime: null, reqSlaTime: null });
  };

  const handleChange = (event: any, newValue: any = '', type: string = '') => {
    if (type === 'Market' || type === 'Process') {
      handleProcessChange(event, newValue, type);
      return;
    }
    if (projectOptionList.length && newValue?.id) {
      setIsClarityProject(true);
    }
    if (newValue === null) {
      setIsDisabled({
        description: false,
        owner: false,
      });
      if (!isClarityProject && type === 'projectName') {
        setFormData({
          ...formData,
          projectName: '',
          projectOwner: '',
          projectDescription: '',
        });
      }

      return;
    }
    if (!type) {
      const name = event?.target?.name;
      const value = event?.target?.type === 'checkbox'
        ? event.target.checked
        : event?.target?.value;
      setFormData({
        ...formData,
        [name]: value,
      });
      return;
    }

    const typeHandlers: { [key: string]: (newValue?: any) => void } = {
      projectName: () => handleProjectNameChange(newValue),
      projectOwner: () => setFormData({ ...formData, projectOwner: newValue || '' }),
      projectDescription: () => setFormData({ ...formData, projectDescription: '' }),
      Sectors: () => handleSectorsChange(event.target.checked, newValue),
      slaTime: () => handleSlaTimeChange(event),
      friendlyName: () => setFormData({ ...formData, friendlyName: event.target.value }),
      slaDate: () => setFormData({ ...formData, slaDate: event.target.value }),
    };

    const handler = typeHandlers[type];
    handler?.();
  };

  const initialFormData = {
    Sectors: [],
    projectName: '',
    projectDescription: '',
    projectOwner: '',
    friendlyName: '',
    dataSteward: '',
    dataOwner: '',
    originalSourceDataAvailabilityCST: '',
    itSourceContact: '',
    dataSourceOfExtraction: '',
    sourceOwnerContact: '',
    gdoStep: '',
    Market: [],
    Process: [],
    origin: '',
    destination: '',
  };

  const handleCancl = (type: string) => {
    setIsClarityProject(false);
    setFormData(initialFormData);
    programData.action = 'CREATE';
    handleEditClose(type);
    getProjectList();
  };
  const handleClose = (type: string) => {
    handleEditClose(type);
    setOpenModal({
      isOpen: false,
    });
  };

  const onCreatenewPrj = (e: any) => {
    const { checked } = e.target;
    setOpen(false);
    setIsClarityProject(false);
    setFormData({
      ...formData,
      projectName: formData.projectName || '',
      projectOwner: '',
      projectDescription: '',
    });
    if (checked) {
      setIsDisabled({
        description: false,
        owner: false,
      });
    } else {
      setIsDisabled({
        description: true,
        owner: true,
      });
    }
  };

  const getLabel = () => {
    return <Box>Stage Name (Origin) </Box>;
  };
  const handleSorting = (sortingInfo: any) => {
    getProjectsByProgram({
      filterInfo: parseFilter(filterInfo),
      currentFilterType,
      page,
      pageSize,
      stDate: startDate,
      edDate: endDate,
      sortingInfo,
      sectorId: getSectorId(),
    });
    setSortingInfo(sortingInfo);
  };

  const handleFilter = (filterInfo: any) => {
    setFilterInfo(filterInfo);
    getProjectsByProgram({
      filterInfo: parseFilter(filterInfo),
      currentFilterType,
      page,
      pageSize,
      stDate: startDate,
      edDate: endDate,
      sortingInfo,
      sectorId: getSectorId(),
    });
  };

  const handleSearch = (e: any) => {
    console.log('FILTER', e);
  };

  // export
  const exportStageList = async () => {
    await exportIbpStageList({ programId: programID });
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);

    const data = getPaginationSlice({
      list: projectsDataList,
      newPage,
      pageSize,
    });

    setProjectList(data);
  };

  const handlepageSizeChange = (event: any) => {
    const newPageSize = Number(event.target.value);
    setPageSize(newPageSize);
    setPage(1);
    const data = projectsDataList.slice(0, newPageSize);
    setProjectList(data);
    setPaginationData({
      ...paginationData,
      totalPages: Math.ceil(projectsDataList.length / newPageSize),
      totalElements: projectsDataList.length,
      currentPage: 1,
    });
  };

  const onCreateProgramClick = (event: any) => {
    setOpenModal({
      ...openModal,
      isOpen: true,
      type: 'Project',
      action: 'CREATE',
    });
  };

  const onProjectSearch = (value: string) => {
    setSearchProject(value);
    debouncedFetchOptionsProjectSearch(value);
  };

  const renderFormActions = () => {
    let buttonText = 'SAVE & ADD MORE';
    if (saveBtnLoader) {
      buttonText = 'SAVING...';
    } else if (programData.action === 'EDIT') {
      buttonText = 'UPDATE & SAVE';
    }

    return (
      <>
        <Button
          onClick={() => handleCancl('close')}
          variant="pepwiseOutline"
          sx={{ color: `${colors.buttons.primary.color} !important` }}
        >
          CANCEL
        </Button>
        <Button disabled={!isProjectFormValid()} type="submit" variant="pepwiseSecondary">
          {buttonText}
        </Button>
      </>
    );
  };

  let deleteMessage;
  if (programData.type === 'Program') {
    deleteMessage = `this ${programData.type}?`;
  } else if (programData.type === 'Project') {
    deleteMessage = (
      <Box component="span">
        this project from{' '}
        <Box component="span" sx={{ fontWeight: '800' }}>
          {programName}
        </Box>
        ?
      </Box>
    );
  } else if (programData?.data && programData.data.length > 1) {
    deleteMessage = `${programData.data.length} jobs from the project?`;
  } else {
    deleteMessage = ' this job from the project?';
  }

  let content;
  if (projectList.length) {
    content = (
      <Box sx={{ position: 'relative' }}>
        {projectList?.length > 0 ? (
          <Box>
            <CustomizedDataGrid
              tableColumns={getStageColumns({ handleAction })}
              tableData={projectList}
              tableType="program-list"
              defaultColumnNames={defaultColumnNames}
              onSorting={handleSorting}
              currSorting={sortingInfo}
              filterDataOptions={filterData}
              onFilterCheck={handleFilter}
              currFilter={filterInfo}
              onFilterSearch={handleSearch}
              page={page}
              pageSize={pageSize}
              paginationData={paginationData}
              listLength={projectsDataList.length}
              handlepageSizeChange={handlepageSizeChange}
              handlePageChange={handlePageChange}
              isFetching={tableLoading}
              showExport={true}
              fileName={`programs_Page_${paginationData.currentPage}_of_${paginationData.totalPages}.xlsx`}
              // isShowDisabledButton={false}
              exportTableData={exportStageList}
              isDateRangeFilter={false}
              isDisabled={false}
              isShowMoreButton
            />
          </Box>
        ) : (
          <Stack justifyContent="center" alignItems="center" height="60vh">
            <Typography variant="pageTitle" color="dark.main">
              No data for the selected filters
            </Typography>
          </Stack>
        )}
      </Box>
    );
  } else {
    content = (
      <ShowInfoWidget
        title="No projects have been added yet"
        height="50vh"
        button=""
        onClick={onCreateProgramClick}
      />
    );
  }

  return (
    <Box ref={formContainerRef} sx={{ mt: 3, padding: '20px', pl: '30px', borderRadius: '8px' }}>
      <Typography
        variant="body2"
        sx={{
          opacity: 0.7,
          mb: 4,
          color: 'textLight.main',
          display: 'flex',
          gap: '4px',
          fontStyle: 'italic',
        }}
      >
        <img src={info} alt="" /> This section is to configure multiple stages.
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={9}>
          <Box component="form" onSubmit={onSubmit} noValidate>
            <DialogContent sx={{ p: 0 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: '16px',
                    }}
                  >
                    <Typography
                      variant="subHeader"
                      color="dark.main"
                      style={{
                        color: 'rgba(0, 155, 216, 1)',
                        display: 'inline-block',
                        backgroundColor: '#F4F8FC',
                        paddingLeft: '4px',
                        paddingRight: '20px',
                        position: 'absolute',
                        zIndex: 2,
                      }}
                    >
                      Select Sector*
                    </Typography>
                    <Divider
                      sx={{ flexGrow: 1, backgroundColor: 'rgba(0, 155, 216, 1)', marginLeft: 10 }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={8}>
                  <FormControl fullWidth>
                    {/* <FormLabel
                      sx={{
                        marginBottom: '10px',
                        fontWeight: '500',
                        color: `${colors.textLight.dark} !important`,
                        '&.Mui-focused': { color: 'white.main' },
                      }}
                    >
                      Select Sectors *
                    </FormLabel> */}
                    <Grid container maxWidth="sm">
                      {loading.sector
                        ? 'Loading...'
                        : sectorOptions.map((sector: string, index: number) => (
                              <Grid item xs={3} key={`${sector}-${index}`}>
                                <FormControlLabel
                                  label={sector}
                                  sx={{
                                    '& .MuiFormControlLabel-label': {
                                      color: `${colors.textLight.main} !important`,
                                    },
                                  }}
                                  control={
                                    isGdoMode ? (
                                      <Radio
                                        checked={formData.Sectors[0] === sector}
                                        onChange={(e) => {
                                          handleChange(e, sector, 'Sectors');
                                        }}
                                        sx={{
                                          '& .MuiSvgIcon-root': {
                                            color: `${colors.dropdown.checkBox} !important`,
                                          },
                                        }}
                                      />
                                    ) : (
                                      <Checkbox
                                        checked={formData.Sectors.includes(sector)}
                                        onChange={(e) => {
                                          handleChange(e, sector, 'Sectors');
                                        }}
                                        sx={{
                                          '& .MuiSvgIcon-root': {
                                            color: `${colors.dropdown.checkBox} !important`,
                                          },
                                        }}
                                      />
                                    )
                                  }
                                />
                              </Grid>
                            ))}
                    </Grid>
                  </FormControl>
                </Grid>

                {isGdoMode ? (
                  <>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          mb: '16px',
                        }}
                      >
                        <Typography
                          variant="subHeader"
                          color="dark.main"
                          style={{
                            color: 'rgba(0, 155, 216, 1)',
                            display: 'inline-block',
                            backgroundColor: '#F4F8FC',
                            paddingLeft: '4px',
                            paddingRight: '20px',
                            position: 'absolute',
                            zIndex: 2,
                          }}
                        >
                          Select Markets*
                        </Typography>
                        <Divider
                          sx={{
                            flexGrow: 1,
                            backgroundColor: 'rgba(0, 155, 216, 1)',
                            marginLeft: 20,
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={8}>
                      <FormControl fullWidth>
                        <Grid container maxWidth="sm">
                          {lookupOptions.markets.map((market, index) => (
                            <Grid item xs={3} key={`${market}-${index}`}>
                              <FormControlLabel
                                label={market}
                                sx={{
                                  '& .MuiFormControlLabel-label': {
                                    color: `${colors.textLight.main} !important`,
                                  },
                                }}
                                control={
                                  <Checkbox
                                    checked={formData.Market?.some(
                                      (item: any) => String(item).trim().toLowerCase() === String(market).trim().toLowerCase(),
                                    )}
                                    onChange={(e) => {
                                      handleChange(e, market, 'Market');
                                    }}
                                    sx={{
                                      '& .MuiSvgIcon-root': {
                                        color: `${colors.dropdown.checkBox} !important`,
                                      },
                                    }}
                                  />
                                }
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          mb: '16px',
                        }}
                      >
                        <Typography
                          variant="subHeader"
                          color="dark.main"
                          style={{
                            color: 'rgba(0, 155, 216, 1)',
                            display: 'inline-block',
                            backgroundColor: '#F4F8FC',
                            paddingLeft: '4px',
                            paddingRight: '20px',
                            position: 'absolute',
                            zIndex: 2,
                          }}
                        >
                          Select Reports*
                        </Typography>
                        <Divider
                          sx={{
                            flexGrow: 1,
                            backgroundColor: 'rgba(0, 155, 216, 1)',
                            marginLeft: 20,
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={8}>
                      <FormControl fullWidth>
                        <Grid container maxWidth="sm">
                          {lookupOptions.reports.map((report, index) => (
                            <Grid item xs={3} key={`${report}-${index}`}>
                              <FormControlLabel
                                label={report}
                                sx={{
                                  '& .MuiFormControlLabel-label': {
                                    color: `${colors.textLight.main} !important`,
                                  },
                                }}
                                control={
                                  <Checkbox
                                    checked={formData.Process?.some(
                                      (item: any) => String(item).trim().toLowerCase() === String(report).trim().toLowerCase(),
                                    )}
                                    onChange={(e) => {
                                      handleChange(e, report, 'Process');
                                    }}
                                    sx={{
                                      '& .MuiSvgIcon-root': {
                                        color: `${colors.dropdown.checkBox} !important`,
                                      },
                                    }}
                                  />
                                }
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </FormControl>
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          mb: '16px',
                        }}
                      >
                        <Typography
                          variant="subHeader"
                          color="dark.main"
                          style={{
                            color: 'rgba(0, 155, 216, 1)',
                            display: 'inline-block',
                            paddingRight: '20px',
                            position: 'absolute',
                            zIndex: 2,
                          }}
                        >
                          Select Report
                        </Typography>
                        <Divider
                          sx={{
                            flexGrow: 1,
                            backgroundColor: 'rgba(0, 155, 216, 1)',
                            marginLeft: 20,
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={8}>
                      <FormControl fullWidth>
                        <Grid container maxWidth="sm">
                          {processList.map((sector: any) => (
                            <Grid item xs={3} key={sector.id}>
                              <FormControlLabel
                                label={sector.name}
                                sx={{
                                  '& .MuiFormControlLabel-label': {
                                    color: `${colors.textLight.main} !important`,
                                  },
                                }}
                                control={
                                  <Checkbox
                                    checked={formData.Process?.includes(sector.name)}
                                    onChange={(e) => {
                                      handleChange(e, sector.name, 'Process');
                                    }}
                                    sx={{
                                      '& .MuiSvgIcon-root': {
                                        color: `${colors.dropdown.checkBox} !important`,
                                      },
                                    }}
                                  />
                                }
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          mb: '16px',
                        }}
                      >
                        <Typography
                          variant="subHeader"
                          color="dark.main"
                          style={{
                            color: 'rgba(0, 155, 216, 1)',
                            display: 'inline-block',
                            paddingRight: '20px',
                            position: 'absolute',
                            zIndex: 2,
                          }}
                        >
                          Select Markets
                        </Typography>
                        <Divider
                          sx={{
                            flexGrow: 1,
                            backgroundColor: 'rgba(0, 155, 216, 1)',
                            marginLeft: 20,
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={8}>
                      <FormControl fullWidth>
                        <Grid container maxWidth="sm">
                          {marketList.map((sector: any) => (
                            <Grid item xs={3} key={sector.id}>
                              <FormControlLabel
                                label={sector.name}
                                sx={{
                                  '& .MuiFormControlLabel-label': {
                                    color: `${colors.textLight.main} !important`,
                                  },
                                }}
                                control={
                                  <Checkbox
                                    checked={formData.Market?.includes(sector.name)}
                                    onChange={(e) => {
                                      handleChange(e, sector.name, 'Market');
                                    }}
                                    sx={{
                                      '& .MuiSvgIcon-root': {
                                        color: `${colors.dropdown.checkBox} !important`,
                                      },
                                    }}
                                  />
                                }
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </FormControl>
                    </Grid>
                  </>
                )}

                {isGdoMode && (
                  <>
                    <Grid item xs={6}>
                      <TextField
                        variant="outlined"
                        label="Origin"
                        name="origin"
                        value={formData.origin || ''}
                        onChange={handleChange}
                        size="small"
                        fullWidth
                        required
                        sx={stageInputSx}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        variant="outlined"
                        label="Destination"
                        name="destination"
                        value={formData.destination || ''}
                        onChange={handleChange}
                        size="small"
                        fullWidth
                        required
                        sx={stageInputSx}
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={6}>
                  {isGdoMode ? (
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Stage Inbound/Outbound to O9"
                      name="projectName"
                      value={formData.projectName || ''}
                      onChange={handleChange}
                      required
                      sx={stageInputSx}
                      SelectProps={{
                        IconComponent: ExpandMoreIcon,
                        MenuProps: {
                          PaperProps: {
                            sx: {
                              backgroundColor: '#FFFFFF !important',
                              color: '#003771 !important',
                              border: '1px solid #D2E6F9',
                              boxShadow: '0px 4px 16px rgba(0, 55, 113, 0.08)',
                              '& .MuiList-root, & .MuiMenu-list': {
                                backgroundColor: '#FFFFFF !important',
                                paddingTop: 0,
                                paddingBottom: 0,
                              },
                              '& .MuiMenuItem-root': {
                                color: '#003771 !important',
                                backgroundColor: '#FFFFFF !important',
                                '&:hover': { backgroundColor: '#F4F8FC !important' },
                                '&.Mui-selected': { backgroundColor: '#D8F0FA !important' },
                                '&.Mui-selected:hover': { backgroundColor: '#D8F0FA !important' },
                              },
                            },
                          },
                        },
                      }}
                    >
                      <MenuItem value="Inbound">Inbound</MenuItem>
                      <MenuItem value="Outbound">Outbound</MenuItem>
                      <MenuItem value="Both">Both</MenuItem>
                    </TextField>
                  ) : (
                    <FormControl fullWidth>
                      <CustomAutocomplete
                        textFieldLabel={getLabel()}
                        loading={dropdownLoading.project}
                        options={projectOptionList}
                        open={open && programData.action !== 'EDIT'}
                        onOpen={() => setOpen(true)}
                        onClose={() => setOpen(false)}
                        value={formData.projectName || ''}
                        disabled={programData.action == 'EDIT' && isClarityProject}
                        onInputChange={(e, val) => {
                          if (val) {
                            const trimmedValue = val.replace(/^\s+/, '');
                            handleInputChange(e, 'projectName', trimmedValue);
                          }
                        }}
                        onChange={(e, val) => handleChange(e, val, 'projectName')}
                        noOptionsText={
                          <Typography
                            sx={{ cursor: 'pointer', color: `${colors.input.text} !important` }}
                            onClick={() => {
                              onCreatenewPrj({ target: { checked: true } });
                            }}
                          >
                            No Stage found <br />
                            Create New Stage
                          </Typography>
                        }
                        sx={getInputCustomCommonSx(colors)}
                        componentsProps={getAutocompleteCommonComponentsProps(colors)}
                        customTextFieldProps={getCommonCustomTextFieldProps(colors)}
                      />
                    </FormControl>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    id="FriendlyName"
                    label="Alias (Friendly Name)"
                    name="friendlyName"
                    variant="outlined"
                    size="small"
                    fullWidth
                    required
                    autoComplete="off"
                    value={formData.friendlyName}
                    error={formData.friendlyName?.length > 45}
                    helperText={
                      formData.friendlyName?.length > 45
                        ? "Friendly Name can't contain more than 45 characters."
                        : undefined
                    }
                    onChange={(e) => handleChange(e, '', 'friendlyName')}
                    onBlur={(e) => handleOnBlur(e, 'friendlyName')}
                    onKeyDown={validateAliasInput}
                    onPaste={handlePaste}
                    sx={stageInputSx}
                  />
                </Grid>

                {getProjectOwnerField({
                  isClarityProject,
                  programData,
                  formData,
                  handleChange,
                  handleInputChange,
                  ownerList,
                  isDisabled,
                  dropdownLoading,
                  colors,
                  isGdoMode,
                  programName,
                })}

                <Grid item xs={6}>
                  <CustomTextField
                    id="ProjectDescription"
                    label="Description"
                    name="projectDescription"
                    variant="outlined"
                    size="small"
                    required
                    autoComplete="off"
                    disabled={isDisabled.description || isClarityProject}
                    value={formData.projectDescription}
                    onChange={handleChange}
                    sx={{
                      ...commonInputSx,
                      ...stageInputSx,
                      '& .MuiOutlinedInput-root': { height: 40 },
                      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#009BD8 !important',
                      },
                    }}
                  />
                </Grid>

                {isGdoMode && (
                  <>
                    <Grid item xs={6}>
                      <TextField fullWidth size="small" label="Data Steward" name="dataSteward" value={formData.dataSteward || ''} onChange={handleChange} sx={stageInputSx} />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField fullWidth size="small" label="Data Owner" name="dataOwner" value={formData.dataOwner || ''} onChange={handleChange} sx={stageInputSx} />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField fullWidth size="small" label="Original Source Data Availability CST" name="originalSourceDataAvailabilityCST" value={formData.originalSourceDataAvailabilityCST || ''} onChange={handleChange} sx={stageInputSx} />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField fullWidth size="small" label="IT Source Contact" name="itSourceContact" value={formData.itSourceContact || ''} onChange={handleChange} sx={stageInputSx} />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField fullWidth size="small" label="Data Source of Extraction" name="dataSourceOfExtraction" value={formData.dataSourceOfExtraction || ''} onChange={handleChange} sx={stageInputSx} />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField fullWidth size="small" label="Source Owner Contact" name="sourceOwnerContact" value={formData.sourceOwnerContact || ''} onChange={handleChange} sx={stageInputSx} />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="GDO Step"
                        name="gdoStep"
                        value={formData.gdoStep || ''}
                        onChange={handleChange}
                        sx={stageInputSx}
                      />
                    </Grid>
                    <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <DialogActions sx={{ p: 0, gap: 1 }}>
                        {renderFormActions()}
                      </DialogActions>
                    </Grid>
                  </>
                )}
              </Grid>
            </DialogContent>

            {!isGdoMode && <DialogActions>{renderFormActions()}</DialogActions>}
          </Box>
          <Box
            p={2}
            sx={{
              backgroundColor: `${colors.modal.bg} !important`,
              color: `${colors.modal.text} !important`,
            }}
          >
            <Dialog
              open={openModal.isOpen}
              onClose={() => handleClose('close')}
              fullWidth
              maxWidth="sm"
              PaperProps={{
                sx: {
                  borderRadius: '4px',
                  overflow: 'hidden',
                  backgroundColor: '#F9FCFF',
                },
              }}
            >
              <DialogTitle
                sx={{
                  backgroundColor: '#003771',
                  color: '#FFFFFF',
                  fontSize: '16px',
                  fontWeight: 600,
                  py: 1.25,
                  pr: 2,
                }}
              >
                Deleting {programData.type === 'Project' ? 'Stage' : programData.type}
                <IconButton
                  aria-label="Close delete confirmation"
                  onClick={() => handleClose('close')}
                  sx={{ color: '#FFFFFF', float: 'right', p: 0 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </DialogTitle>
              <DialogContent sx={{ px: 4, py: 3, backgroundColor: '#F9FCFF' }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box component="img" src={DeleteImage} alt="" sx={{ width: 64, height: 64 }} />
                  <Stack spacing={1}>
                    <Typography sx={{ color: '#111111', fontSize: '20px', fontWeight: 600 }}>
                      Are you sure to delete {deleteMessage}
                    </Typography>
                    <Typography sx={{ color: '#333333', fontSize: '14px' }}>
                      Once confirmed, the action can&apos;t be undone.
                    </Typography>
                  </Stack>
                </Stack>
              </DialogContent>
              <DialogActions sx={{ px: 4, pb: 3, backgroundColor: '#F9FCFF' }}>
                <Button
                  onClick={() => handleClose('close')}
                  sx={{ color: '#009BD8', fontWeight: 700 }}
                >
                  NO, I WANT IT!
                </Button>
                <Button
                  onClick={onSubmit}
                  variant="contained"
                  sx={{
                    backgroundColor: '#FF2B2B',
                    color: '#FFFFFF',
                    fontWeight: 700,
                    '&:hover': { backgroundColor: '#D92121' },
                  }}
                >
                  {saveBtnLoader ? 'DELETING...' : 'DELETE'}
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        </Grid>
      </Grid>
      <Box pt={0} pr={2} pb={3} pl={0} mt={1}>
        <Box sx={{ position: 'relative' }}>
          <Divider sx={{ flexGrow: 1, backgroundColor: 'rgba(0, 155, 216, 1)', marginLeft: 0 }} />
          <Typography
            mt={3}
            variant="widgetTitle"
            sx={{ position: 'absolute', color: `${colors.textLight.main} !important` }}
            component="span"
          >
            {' '}
            Stages
          </Typography>
          <CustomTextField
            placeholder="Search stages"
            value={searchProject}
            onChange={(e) => onProjectSearch(e.target.value)}
            style={{
              width: '22rem',
              position: 'absolute',
              top: '23px',
              right: '50px',
              backgroundColor: '#F4F7FA',
              borderRadius: '4px',
              zIndex: '99',
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#F6FBFD !important',
                borderRadius: '4px',
                '& fieldset': {
                  borderColor: '#6F8A99 !important',
                },
                '&:hover fieldset': {
                  borderColor: '#6F8A99 !important',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#6F8A99 !important',
                },
              },
              '& .MuiInputBase-input': {
                color: '#0B3A56 !important',
                WebkitTextFillColor: '#0B3A56 !important',
                fontSize: '13px !important',
                padding: '14px !important',
                paddingLeft: '0 !important',
              },
              '& input': {
                color: '#0B3A56 !important',
                WebkitTextFillColor: '#0B3A56 !important',
                opacity: '1 !important',
              },
              '& .MuiInputBase-input::placeholder': {
                color: '#6F8A99 !important',
                opacity: '1 !important',
              },
              '& .MuiInputAdornment-root, & .MuiInputAdornment-root svg': {
                color: '#6F8A99 !important',
                fill: '#6F8A99 !important',
                fontSize: '18px',
              },
            }}
            InputLabelProps={{ sx: { backgroundColor: 'transparent' } }}
            InputProps={{
              sx: { height: 36 },
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: '#6F8A99 !important', fontSize: '18px' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        {content}
      </Box>
    </Box>
  );
};
